import React from "react";
import {
  CalendarDays,
  ChevronDown,
  FileDown,
  Filter,
  LoaderCircle,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Trash2,
  X,
} from "lucide-react";
import { LoadingClientAutocompleteInput } from "./DataManagerOrdersPrimitives.jsx";
import DataManagerDispatchDatePickerInput from "./DataManagerDispatchDatePickerInput.jsx";
import DataManagerDispatchDayNotice from "./DataManagerDispatchDayNotice.jsx";
import { HoverCard } from "./ResultsPrimitives.jsx";
import { ORDER_ADVANCED_FILTER_CATALOG } from "../utils/dataManagerOrdersCustomization.js";
import { isDispatchDayCalendarPopoverTarget } from "../utils/dispatchDayCalendarUtils.js";
import { isLoadingClientSuggestionsTarget } from "../hooks/useDataManagerOrdersUtils.js";

const SECONDARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.75 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-100";
const DANGER_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.75 text-[11px] font-semibold text-rose-700 transition-colors duration-150 hover:border-rose-300 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60";
const APPROVAL_FILTER_ACTIVE_CLASS_NAME = "inline-flex h-9 items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.75 text-[11px] font-semibold text-amber-800 transition-colors duration-150 hover:border-amber-400 hover:bg-amber-100";
const APPROVAL_FILTER_INACTIVE_CLASS_NAME = "inline-flex h-9 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.75 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900";
const FILTER_TRIGGER_ACTIVE_CLASS_NAME = "inline-flex h-9 items-center gap-1 rounded-full border border-accent bg-accent px-2.75 text-[11px] font-semibold text-white transition-colors duration-150 hover:border-accent-strong hover:bg-accent-strong";
const FILTER_TRIGGER_INACTIVE_CLASS_NAME = "inline-flex h-9 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.75 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900";
const PANEL_PICKER_INPUT_CLASS_NAME = "h-9 w-full appearance-none rounded-sm border border-slate-200 bg-white py-2 pl-3 pr-8 text-[12px] text-slate-700 outline-hidden transition-colors duration-150 focus:border-accent [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0";
const PANEL_PICKER_BUTTON_CLASS_NAME = "absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-sm border-l border-slate-200 bg-slate-50 text-slate-500 transition-colors duration-150 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300";
const FILTER_INPUT_CLASS_NAME = "h-8 w-full rounded-[10px] border border-slate-200 bg-white px-2.5 text-[11.5px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent";
const FILTER_SELECT_CLASS_NAME = FILTER_INPUT_CLASS_NAME;
const ACTIVE_FILTER_CHIP_CLASS_NAME = "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-accent bg-accent px-2 text-[10.5px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong";
const NEUTRAL_FILTER_CHIP_CLASS_NAME = "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-slate-200 bg-white px-2 text-[10.5px] font-semibold text-slate-700";
const FILTER_CHECKBOX_CLASS_NAME = "h-4 w-4 shrink-0 rounded border-slate-300 text-accent focus:ring-accent";

function ActiveFilterChip({ label, onRemove = null, value = "" }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className={ACTIVE_FILTER_CHIP_CLASS_NAME}
      title={`Retirer le filtre ${label}`}
    >
      <span className="truncate">{label}{value ? `: ${value}` : ""}</span>
      <X size={11} aria-hidden="true" className="shrink-0 text-white/80" />
    </button>
  );
}

function AdvancedFilterControl({
  draftAdvancedFilters = {},
  filter,
  loadingClientOptions = [],
  onDraftChange = null,
}) {
  if (!filter) {
    return null;
  }

  if (filter.type === "loading-client") {
    return (
      <label className="relative grid gap-1 overflow-visible">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{filter.label}</span>
        <LoadingClientAutocompleteInput
          className={FILTER_INPUT_CLASS_NAME}
          listId={`orders-advanced-${filter.key}`}
          onValueChange={(nextValue) => onDraftChange?.(filter.key, nextValue)}
          options={loadingClientOptions}
          placeholder="Nom client"
          value={draftAdvancedFilters[filter.key] || ""}
        />
      </label>
    );
  }

  if (filter.type === "boolean") {
    return (
      <label className="grid gap-1">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{filter.label}</span>
        <select
          className={FILTER_SELECT_CLASS_NAME}
          value={draftAdvancedFilters[filter.key] || ""}
          onChange={(event) => onDraftChange?.(filter.key, event.target.value)}
        >
          <option value="">Tous</option>
          <option value="oui">Oui</option>
          <option value="non">Non</option>
        </select>
      </label>
    );
  }

  if (filter.type === "enum") {
    return (
      <label className="grid gap-1">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{filter.label}</span>
        <select
          className={FILTER_SELECT_CLASS_NAME}
          value={draftAdvancedFilters[filter.key] || ""}
          onChange={(event) => onDraftChange?.(filter.key, event.target.value)}
        >
          <option value="">Tous</option>
          {filter.options?.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
    );
  }

  if (filter.type === "number-range") {
    return (
      <label className="grid gap-1">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{filter.label}</span>
        <span className="grid grid-cols-2 gap-2">
          <input
            type="number"
            step="0.1"
            min="0"
            className={FILTER_INPUT_CLASS_NAME}
            placeholder="Min"
            value={draftAdvancedFilters[`${filter.key}_min`] || ""}
            onChange={(event) => onDraftChange?.(`${filter.key}_min`, event.target.value)}
          />
          <input
            type="number"
            step="0.1"
            min="0"
            className={FILTER_INPUT_CLASS_NAME}
            placeholder="Max"
            value={draftAdvancedFilters[`${filter.key}_max`] || ""}
            onChange={(event) => onDraftChange?.(`${filter.key}_max`, event.target.value)}
          />
        </span>
      </label>
    );
  }

  if (filter.type === "date-range") {
    return (
      <label className="grid gap-1">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{filter.label}</span>
        <span className="grid grid-cols-2 gap-2">
          <DataManagerDispatchDatePickerInput
            buttonClassName="absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-[10px] border-l border-slate-200 bg-slate-50 text-slate-500 transition-colors duration-150 hover:text-slate-700"
            inputClassName={FILTER_INPUT_CLASS_NAME}
            onChange={(event) => onDraftChange?.(`${filter.key}_min`, event.target.value)}
            placeholder="Du"
            popoverPositioning="floating"
            value={draftAdvancedFilters[`${filter.key}_min`] || ""}
          />
          <DataManagerDispatchDatePickerInput
            buttonClassName="absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-[10px] border-l border-slate-200 bg-slate-50 text-slate-500 transition-colors duration-150 hover:text-slate-700"
            inputClassName={FILTER_INPUT_CLASS_NAME}
            onChange={(event) => onDraftChange?.(`${filter.key}_max`, event.target.value)}
            placeholder="Au"
            popoverPositioning="floating"
            value={draftAdvancedFilters[`${filter.key}_max`] || ""}
          />
        </span>
      </label>
    );
  }

  return (
    <label className="grid gap-1">
      <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{filter.label}</span>
      <input
        type="text"
        className={FILTER_INPUT_CLASS_NAME}
        placeholder={filter.label}
        value={draftAdvancedFilters[filter.key] || ""}
        onChange={(event) => onDraftChange?.(filter.key, event.target.value)}
      />
    </label>
  );
}

function AdvancedFiltersPopover({
  advancedFilters = {},
  draftAdvancedFilters = {},
  draftVisibleFilterKeys = [],
  isCustomizingFilters = false,
  isBrowseMode = false,
  isOpen = false,
  loadingClientOptions = [],
  onApply = null,
  onClose = null,
  onDraftChange = null,
  onResetFilterSettings = null,
  onReset = null,
  onSaveFilterSettings = null,
  onToggleFilterSettings = null,
  onToggleVisibleFilter = null,
  popoverRef = null,
  visibleFilterKeys = [],
}) {
  if (!isOpen) {
    return null;
  }
  const visibleFilterKeySet = new Set(visibleFilterKeys);
  const draftVisibleFilterKeySet = new Set(draftVisibleFilterKeys);
  const visibleFilters = ORDER_ADVANCED_FILTER_CATALOG.filter((filter) => visibleFilterKeySet.has(filter.key));

  return (
    <div
      ref={popoverRef}
      role="dialog" aria-modal="false" aria-label="Filtres avancés"
      className="absolute left-0 top-[calc(100%+0.35rem)] z-30 w-[min(26rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)]"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Filtres avancés</p>
          <p className="mt-1 text-[11px] leading-5 text-slate-600">{isCustomizingFilters ? "Choisissez les filtres à afficher dans cette fenêtre." : "Affinez la grille sans quitter la recherche principale."}</p>
        </div>
        <button type="button" onClick={onClose} className={SECONDARY_ACTION_CLASS_NAME} aria-label="Fermer les filtres avancés">
          <X size={13} />
        </button>
      </div>

      {isCustomizingFilters ? (
        <div className="grid max-h-[18rem] gap-1 overflow-y-auto pr-1">
          {ORDER_ADVANCED_FILTER_CATALOG.map((filter) => (
            <label key={filter.key} className="flex items-center gap-2 border-b border-slate-100 py-1.5 text-[11.5px] font-semibold text-slate-700 last:border-b-0">
              <input
                type="checkbox"
                checked={draftVisibleFilterKeySet.has(filter.key)}
                onChange={(event) => onToggleVisibleFilter?.(filter.key, event.target.checked)}
                className={FILTER_CHECKBOX_CLASS_NAME}
              />
              <span>{filter.label}</span>
            </label>
          ))}
        </div>
      ) : (
        <div className="grid gap-2.5 overflow-visible">
          {visibleFilters.length > 0 ? (
            visibleFilters.map((filter) => (
              <AdvancedFilterControl
                key={filter.key}
                draftAdvancedFilters={draftAdvancedFilters}
                filter={filter}
                loadingClientOptions={loadingClientOptions}
                onDraftChange={onDraftChange}
              />
            ))
          ) : (
            <p className="rounded-[10px] border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px] leading-5 text-slate-500">Aucun filtre affiché.</p>
          )}

          <label className="grid gap-1">
            <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Scope</span>
            <select
              className={FILTER_SELECT_CLASS_NAME}
              value={isBrowseMode ? "browse" : "dispatch"}
              onChange={(event) => onDraftChange?.("scope", event.target.value)}
            >
              <option value="dispatch">Dispatch courant</option>
              <option value="browse">Toutes les commandes</option>
            </select>
          </label>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
        {isCustomizingFilters ? (
          <>
            <button type="button" onClick={onResetFilterSettings} className={SECONDARY_ACTION_CLASS_NAME}>
              Réinitialiser
            </button>
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" onClick={() => onToggleFilterSettings?.(false)} className={SECONDARY_ACTION_CLASS_NAME}>
                Annuler
              </button>
              <button type="button" onClick={onSaveFilterSettings} className={FILTER_TRIGGER_ACTIVE_CLASS_NAME}>
                Enregistrer
              </button>
            </div>
          </>
        ) : (
          <>
            <button type="button" onClick={onReset} className={SECONDARY_ACTION_CLASS_NAME}>
              Réinitialiser
            </button>
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" onClick={() => onToggleFilterSettings?.(true)} className={SECONDARY_ACTION_CLASS_NAME}>
                <SlidersHorizontal size={13} />
                Configurer
              </button>
              <button type="button" onClick={onApply} className={FILTER_TRIGGER_ACTIVE_CLASS_NAME}>
                Appliquer
              </button>
            </div>
          </>
        )}
      </div>

      {Object.values(advancedFilters).some(Boolean) ? (
        <p className="mt-2 text-[10px] leading-5 text-slate-500">
          Filtres actifs appliqués sur la grille visible.
        </p>
      ) : null}
    </div>
  );
}

export default function DataManagerOrdersGridHeader({
  activeFilterChips = [],
  advancedFilters = {},
  canApplyEndOfDayLoadGroup = false,
  bulkBillingApprovalSelectionMeta = null,
  draftVisibleFilterKeys = [],
  draftAdvancedFilters = {},
  endOfDayLoadGroupReason = "",
  hasAdvancedFiltersDraft = false,
  isAdvancedFiltersOpen = false,
  isCustomizingFilters = false,
  isBrowseMode = false,
  isCreatingOrder = false,
  isDeletingSelection = false,
  isBulkBillingApprovalPending = false,
  isPendingBillingApprovalFilter = false,
  isEndOfDayLoadPending = false,
  isRecentBrowseLimited = false,
  loadingClientOptions = [],
  onApplyAdvancedFilters = null,
  onClearAllFilters = null,
  onClearSearchQuery = null,
  onDraftAdvancedFilterChange = null,
  onExportPdf = null,
  deleteSelectionBlockedReason = "",
  onResetFilterSettings = null,
  onOpenEndOfDayLoadGroupDialog = null,
  onOpenBulkBillingApproval = null,
  onOpenOrderBrowser = null,
  onQuickSelectOrderBrowseDate = null,
  onRemoveActiveFilter = null,
  onResetAdvancedFilters = null,
  onResetOrderScope = null,
  onSaveFilterSettings = null,
  onSearchQueryChange = null,
  onToggleAdvancedFilters = null,
  onToggleAllVisibleOrderSelections = null,
  onToggleFilterSettings = null,
  onTogglePendingBillingApprovalFilter = null,
  onToggleVisibleFilter = null,
  deliveryDatePickerValue = "",
  resultSummaryLabel = "",
  searchInputRef = null,
  searchQuery = "",
  selectedDeleteCount = 0,
  selectedOrderCount = 0,
  pendingBillingApprovalCount = 0,
  showBulkDeleteControls = false,
  showBulkBillingApprovalControls = false,
  showEndOfDayLoadGroupControls = false,
  visibleFilterKeys = [],
  visibleOrderCount = 0,
}) {
  const advancedFiltersRef = React.useRef(null);
  const filtersButtonRef = React.useRef(null);
  const trimmedSearchQuery = typeof searchQuery === "string" ? searchQuery.trim() : "";
  const hasActiveFilters = activeFilterChips.length > 0;
  const hasAdvancedFiltersApplied = Object.values(advancedFilters).some(Boolean);
  const bulkBillingApprovalDisabled = isBulkBillingApprovalPending
    || bulkBillingApprovalSelectionMeta?.canOpenPreflight !== true;
  const bulkBillingApprovalDisabledReason = isBulkBillingApprovalPending
    ? "La sélection est en cours de vérification."
    : bulkBillingApprovalSelectionMeta?.disabledReason || "Sélectionnez les commandes à vérifier dans Consolidation.";
  const bulkBillingApprovalButton = (
    <button
      type="button"
      onClick={() => onOpenBulkBillingApproval?.()}
      disabled={bulkBillingApprovalDisabled}
      className={`${SECONDARY_ACTION_CLASS_NAME} !rounded-[4px]${bulkBillingApprovalDisabled ? " pointer-events-none" : ""}`}
    >
      {isBulkBillingApprovalPending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck size={13} />}
      {bulkBillingApprovalSelectionMeta?.buttonLabel || "Approuver la sélection"}
    </button>
  );

  React.useEffect(() => {
    if (!isAdvancedFiltersOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (advancedFiltersRef.current?.contains(target) || filtersButtonRef.current?.contains(target)) {
        return;
      }
      if (isDispatchDayCalendarPopoverTarget(target)) {
        return;
      }
      if (isLoadingClientSuggestionsTarget(target)) {
        return;
      }
      onToggleAdvancedFilters?.(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isAdvancedFiltersOpen, onToggleAdvancedFilters]);

  return (
    <div className="border-b border-slate-200 px-2.5 py-1.5">
      <div className="flex flex-wrap items-end gap-x-2 gap-y-2">
        <label className="min-w-[280px] flex-[1_1_280px] max-w-[560px]">
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Rechercher une commande</span>
          <div className="relative mt-1">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={(event) => onSearchQueryChange?.(event.target.value)}
              placeholder="N° commande, bon de travail, client, silo, produit, quantité…"
              className="h-9 w-full rounded-sm border border-slate-200 bg-white py-2 pl-9 pr-8 text-[12px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent"
            />
            {trimmedSearchQuery ? (
              <button
                type="button"
                onClick={onClearSearchQuery}
                className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-slate-400 transition-colors duration-150 hover:text-slate-700"
                aria-label="Effacer la recherche"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        </label>

        <label className="w-44 shrink-0">
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Date de livraison</span>
          <div className="mt-1">
            <DataManagerDispatchDatePickerInput
              buttonClassName={PANEL_PICKER_BUTTON_CLASS_NAME}
              inputClassName={PANEL_PICKER_INPUT_CLASS_NAME}
              onChange={(event) => onQuickSelectOrderBrowseDate?.(event.target.value)}
              placeholder="jj-mm-aaaa"
              value={deliveryDatePickerValue}
            />
          </div>
        </label>

        <div className="relative shrink-0">
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-transparent select-none" aria-hidden="true">Filtres</span>
          <button
            ref={filtersButtonRef}
            type="button"
            onClick={() => onToggleAdvancedFilters?.(!isAdvancedFiltersOpen)}
            className={hasAdvancedFiltersApplied || hasAdvancedFiltersDraft ? FILTER_TRIGGER_ACTIVE_CLASS_NAME : FILTER_TRIGGER_INACTIVE_CLASS_NAME}
            aria-expanded={isAdvancedFiltersOpen}
          >
            <Filter size={13} aria-hidden="true" />
            Filtres
            <ChevronDown size={12} aria-hidden="true" className={isAdvancedFiltersOpen ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
          <AdvancedFiltersPopover
            advancedFilters={advancedFilters}
            draftVisibleFilterKeys={draftVisibleFilterKeys}
            draftAdvancedFilters={draftAdvancedFilters}
            isCustomizingFilters={isCustomizingFilters}
            isBrowseMode={isBrowseMode}
            isOpen={isAdvancedFiltersOpen}
            loadingClientOptions={loadingClientOptions}
            onApply={onApplyAdvancedFilters}
            onClose={() => onToggleAdvancedFilters?.(false)}
            onDraftChange={onDraftAdvancedFilterChange}
            onResetFilterSettings={onResetFilterSettings}
            onReset={onResetAdvancedFilters}
            onSaveFilterSettings={onSaveFilterSettings}
            onToggleFilterSettings={onToggleFilterSettings}
            onToggleVisibleFilter={onToggleVisibleFilter}
            popoverRef={advancedFiltersRef}
            visibleFilterKeys={visibleFilterKeys}
          />
        </div>

        <div className="flex flex-col justify-end shrink-0">
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-transparent select-none" aria-hidden="true">Approbation</span>
          <button
            type="button"
            onClick={() => onTogglePendingBillingApprovalFilter?.()}
            className={isPendingBillingApprovalFilter ? APPROVAL_FILTER_ACTIVE_CLASS_NAME : APPROVAL_FILTER_INACTIVE_CLASS_NAME}
            title={isPendingBillingApprovalFilter ? "Restaurer la sélection précédente" : "Afficher la file globale des commandes à approuver"}
            aria-pressed={isPendingBillingApprovalFilter}
          >
            <ShieldCheck size={13} aria-hidden="true" />
            À approuver ({Math.max(0, Number(pendingBillingApprovalCount) || 0)})
          </button>
        </div>

        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1.5 self-end">
          {isBrowseMode ? (
            <button type="button" onClick={onResetOrderScope} className={SECONDARY_ACTION_CLASS_NAME}>
              <RotateCcw size={13} />
              Retour dispatch
            </button>
          ) : null}
          <button type="button" onClick={onOpenOrderBrowser} className={SECONDARY_ACTION_CLASS_NAME}>
            <CalendarDays size={13} />
            {isBrowseMode ? "Modifier la recherche" : "Autres commandes"}
          </button>
          {onExportPdf ? (
            <button
              type="button"
              onClick={onExportPdf}
              className={SECONDARY_ACTION_CLASS_NAME}
              title="Exporter les commandes affichées en PDF"
            >
              <FileDown size={13} />
              Exporter PDF
            </button>
          ) : null}
        </div>
      </div>

      <DataManagerDispatchDayNotice dispatchDate={deliveryDatePickerValue} />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-slate-100 pt-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="text-[11px] text-slate-600">
            {visibleOrderCount} commande{visibleOrderCount === 1 ? "" : "s"} affichée{visibleOrderCount === 1 ? "" : "s"}
            {resultSummaryLabel ? ` · ${resultSummaryLabel}` : ""}
            {isRecentBrowseLimited ? " (50 plus récentes)" : ""}
          </p>

          {hasActiveFilters ? (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {activeFilterChips.map((chip) => (
                <ActiveFilterChip
                  key={chip.key}
                  label={chip.label}
                  value={chip.value}
                  onRemove={() => onRemoveActiveFilter?.(chip.key)}
                />
              ))}
              {activeFilterChips.length >= 2 ? (
                <button type="button" onClick={onClearAllFilters} className="text-[10.5px] font-semibold text-slate-500 transition-colors duration-150 hover:text-slate-800">
                  Tout effacer
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {!isCreatingOrder && showBulkDeleteControls && selectedOrderCount > 0 ? (
            <span className={NEUTRAL_FILTER_CHIP_CLASS_NAME}>
              {selectedOrderCount || selectedDeleteCount} sélectionnée(s)
            </span>
          ) : null}
          {showEndOfDayLoadGroupControls && selectedOrderCount > 0 ? (
            <button
              type="button"
              onClick={() => onOpenEndOfDayLoadGroupDialog?.()}
              disabled={!canApplyEndOfDayLoadGroup || isEndOfDayLoadPending}
              className={SECONDARY_ACTION_CLASS_NAME}
              title={canApplyEndOfDayLoadGroup ? "Assigner Chargé en PM pour la sélection" : endOfDayLoadGroupReason}
            >
              {isEndOfDayLoadPending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Sun size={13} aria-hidden="true" />}
              PM sélection
            </button>
          ) : null}
          {showEndOfDayLoadGroupControls && selectedOrderCount > 0 && !canApplyEndOfDayLoadGroup ? (
            <span className="max-w-[14rem] truncate text-[10.5px] font-semibold text-slate-500" title={endOfDayLoadGroupReason}>
              {endOfDayLoadGroupReason}
            </span>
          ) : null}
          {showBulkBillingApprovalControls && selectedOrderCount > 0 ? (
            bulkBillingApprovalDisabled ? (
              <HoverCard
                title={isBulkBillingApprovalPending ? "Vérification en cours" : "Ouverture indisponible"}
                content={(
                  <div className="grid gap-2">
                    <p className="m-0 font-semibold text-slate-700">{bulkBillingApprovalDisabledReason}</p>
                  </div>
                )}
                placement="bottom"
                maxWidth={380}
                referenceAsChild
              >
                <span
                  className="inline-flex"
                  tabIndex={0}
                  aria-label={bulkBillingApprovalDisabledReason}
                >
                  {bulkBillingApprovalButton}
                </span>
              </HoverCard>
            ) : bulkBillingApprovalButton
          ) : null}
          {showBulkDeleteControls && selectedDeleteCount > 0 ? (
            <button
              type="button"
              onClick={() => onToggleAllVisibleOrderSelections?.("delete-selection-dialog")}
              disabled={selectedDeleteCount === 0 || isDeletingSelection || Boolean(deleteSelectionBlockedReason)}
              className={DANGER_ACTION_CLASS_NAME}
              title={deleteSelectionBlockedReason || undefined}
            >
              {isDeletingSelection ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Trash2 size={13} />}
              Supprimer la sélection
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
